import { uniq, uniqBy } from 'lodash'
import { NavigateFunction } from 'react-router-dom'

import { PSApplication } from '../components/app/PSApplication'
import { Category } from '../models/Category'
import { Edition } from '../models/Edition'
import { Item } from '../models/Item'
import { RecordFactory } from '../models/RecordFactory'
import { Type } from '../models/Type'
import { OnboardingState } from '../providers/OnboardingProvider'

type GetNextDefaultPathOpts = {
    edition: Edition
    potentialItemIDs: string[]
    step: Step
    initialStep: Step
    forOnboarding?: boolean
}

export type Step =
    | 'categories'
    | 'services'
    | 'classes'
    | 'reservations'
    | 'packages'
    | 'subscriptions'
    | 'products'
    | 'gifts'
    | 'forms'
    | 'contracts'

const getCurrentStepInfo = (
    step: Step,
    edition: Edition
): { defaultList: Category[] | Type[] | Item[]; nextStep: Step | null } => {
    switch (step) {
        case 'categories':
            return {
                defaultList: edition.defaultCategories,
                nextStep: 'services',
            }
        case 'services':
            return {
                defaultList: edition.defaultServices,
                nextStep: 'classes',
            }
        case 'classes':
            return {
                defaultList: edition.defaultClasses,
                nextStep: 'reservations',
            }
        case 'reservations':
            return {
                defaultList: edition.defaultReservations,
                nextStep: 'packages',
            }
        case 'packages':
            return {
                defaultList: edition.defaultPackages,
                nextStep: 'subscriptions',
            }
        case 'subscriptions':
            return {
                defaultList: edition.defaultSubscriptions,
                nextStep: 'products',
            }
        case 'products':
            return {
                defaultList: edition.defaultProducts,
                nextStep: 'gifts',
            }
        case 'gifts':
            return {
                defaultList: edition.defaultGifts,
                nextStep: 'forms',
            }
        case 'forms':
        case 'contracts':
            return {
                defaultList: [],
                nextStep: null,
            }
    }
}

/**
 * Get the next default step details
 *
 * The idea is that a user can select to install something (category, package, etc) from the edition defaults.
 * However this default item may itself be linked to other defaults (e.g. a package may have a service linked to it).
 * This function will keep traversing the linked defaults until it finds an item that is not installed,
 * at which point it will return the path for that item and the list of not installed items.
 *
 * Example:
 *
 * 1. User selects category 'default-category-4'
 * 2. This category has the following listings: 'default-service-1', 'default-service-2', 'default-package-7'
 * 3. 'default-service-1' is already installed, but 'default-service-2' and 'default-package-7' are not.
 * 4. In the function below, after filtering out installed items, the remaining list is ['default-service-2', 'default-package-7']
 * 5. We check each step and see if any of the proposed, remaining items are in the current step's default list.
 * 6. 'default-service-2' is in the default list for 'services', so we direct the user to /settings/services/defaults?recordIDs=default-service-2,default-package7
 * 7. This service (default-service-2) has a contract (default-contract-3) linked to it, so we need to add this to the list potential items.
 * 8. Repeat this process for all steps
 *    - go to /settings/packages/defaults?recordIDs=default-package-7,default-contract-3
 *    - go to /settings/contracts/defaults?recordIDs=default-contract-3
 * 9. Return to the initial step, since we've already installed all potential items (categories in this example)
 *
 */

const getNextStepDetails = async ({
    edition,
    potentialItemIDs,
    step,
    forOnboarding,
}: GetNextDefaultPathOpts) => {
    const installedItems = await RecordFactory.getInstance().loadItems(PSApplication.userId())

    const itemsNotInstalled = [
        ...new Set(
            potentialItemIDs.filter((id) => !installedItems.some((i) => i.editionItem === id))
        ),
    ]

    let currStep: Step | null = getCurrentStepInfo(step, edition).nextStep

    while (currStep && !['gift', 'form'].includes(currStep)) {
        const { defaultList, nextStep } = getCurrentStepInfo(currStep, edition)

        const doesStepHavePotentialItems =
            potentialItemIDs.filter(
                (item) => defaultList.some((d) => d.id === item) && itemsNotInstalled.includes(item)
            ).length > 0

        if (doesStepHavePotentialItems) {
            // even if we have filtered items, we want to make sure all items are returned for future steps
            // since all uninstalled items should be proposed to the user down the line on their respective pages
            return { path: currStep, recordIDs: itemsNotInstalled }
        }

        currStep = nextStep
    }

    //
    // Forms and Contracts are handled differently
    //

    const allDefaultItems = [
        ...edition.defaultServices,
        ...edition.defaultProducts,
        ...edition.defaultPackages,
        ...edition.defaultSubscriptions,
        ...edition.defaultClasses,
        ...edition.defaultGifts,
        ...edition.defaultReservations,
    ]

    const allOnboardingFormIDs = Array.from(
        new Set(allDefaultItems.map((i) => i.onboardingFormID).filter(Boolean))
    ) as string[]

    const installedForms = await RecordFactory.getInstance().loadTypes(PSApplication.userId())

    const installedFormEditionTypes = installedForms
        .map((f) => f.editionType)
        .filter(Boolean) as string[]

    const uninstalledFormIDs = allOnboardingFormIDs.filter(
        (id) => !installedFormEditionTypes.includes(id) && potentialItemIDs.includes(id)
    )

    const filteredForms = edition.defaultTypes.filter(
        (f) =>
            (f.id && uninstalledFormIDs.includes(f.id)) ||
            (!f.associatedWithService && forOnboarding)
    )

    if (!['forms', 'contracts'].includes(step) && filteredForms.length) {
        return {
            path: 'forms',
            recordIDs: uniq([...itemsNotInstalled, ...filteredForms.map((f) => f.id)]),
        }
    }

    // try contracts if there were no forms

    const allOnboardingContractIDs = Array.from(
        new Set(allDefaultItems.map((i) => i.onboardingContractID).filter(Boolean))
    ) as string[]

    const installedContracts = await RecordFactory.getInstance().loadContracts()

    const installedContractEditionIDs = installedContracts
        .map((c) => c.editionContract)
        .filter(Boolean) as string[]

    const uninstalledContractIDs = allOnboardingContractIDs.filter(
        (id) => !installedContractEditionIDs.includes(id) && potentialItemIDs.includes(id)
    )

    const filteredContracts = edition.defaultContracts.filter(
        (c) =>
            (c.id && uninstalledContractIDs.includes(c.id)) ||
            (forOnboarding && !c.associatedWithService)
    )

    if (step != 'contracts' && filteredContracts.length) {
        return {
            path: 'contracts',
            recordIDs: uniq([...itemsNotInstalled, ...filteredContracts.map((c) => c.id)]),
        }
    }

    return {
        path: null,
        recordIDs: [],
    }
}

const getStepFromOnboardingState = (state: OnboardingState): Step | null => {
    switch (state) {
        case 'ob_categories':
            return 'categories'
        case 'ob_services':
            return 'services'
        case 'ob_classes':
            return 'classes'
        case 'ob_reservations':
            return 'reservations'
        case 'ob_packages':
            return 'packages'
        case 'ob_subscriptions':
            return 'subscriptions'
        case 'ob_products':
            return 'products'
        case 'ob_gifts':
            return 'gifts'
        case 'ob_records':
            return 'forms'
        case 'ob_contracts':
            return 'contracts'
        default:
            return null
    }
}

export const getNextOnboardingStepPath = async ({
    edition,
    potentialItemIDs,
    step,
}: Omit<GetNextDefaultPathOpts, 'step' | 'initialStep' | 'forOnboarding'> & {
    step: OnboardingState
}) => {
    if (!potentialItemIDs.length) {
        return null
    }

    const currStep = getStepFromOnboardingState(step)
    if (!currStep) {
        return null
    }

    const details = await getNextStepDetails({
        edition,
        potentialItemIDs,
        step: currStep,
        initialStep: 'categories',
        forOnboarding: true,
    })

    // did we skip products? we need to ensure that we always show products, the one exception to the flow
    if (
        ['categories', 'services', 'classes', 'reservations', 'packages', 'subscriptions'].includes(
            currStep
        ) &&
        ['gifts', 'forms', 'contracts', null].includes(details.path)
    ) {
        return details.recordIDs
            ? `/onboarding/products?recordIDs=${details.recordIDs.join(',')}`
            : '/onboarding/products'
    }

    if (!details.path) {
        return null
    }

    return details.recordIDs.length
        ? `/onboarding/${details.path}?recordIDs=${details.recordIDs.join(',')}`
        : `/onboarding/${details.path}`
}

export const navigateToNextDefaultStep = async (
    navigate: NavigateFunction,
    { edition, potentialItemIDs, step, initialStep }: GetNextDefaultPathOpts
) => {
    const filteredPotentialItemIDs = potentialItemIDs.filter((i) => i.startsWith('default'))

    if (!filteredPotentialItemIDs.length) {
        return navigate(`/settings/${initialStep}`)
    }

    const details = await getNextStepDetails({
        edition,
        potentialItemIDs: filteredPotentialItemIDs,
        step,
        initialStep,
        forOnboarding: false,
    })

    if (!details.path) {
        return navigate(`/settings/${initialStep}`)
    }

    details.recordIDs.length
        ? navigate(
              `/settings/${details.path}/defaults?initialStep=${initialStep}&recordIDs=${details.recordIDs.join(',')}`
          )
        : navigate(`/settings/${details.path}`)
}
